import { useEffect, useRef, useState } from 'react'
import { Chatgrund } from './Chatgrund'
import { Symbolbild } from './Symbolbild'
import { Zuruecksetzen } from './Zuruecksetzen'
import { Netzstreifen } from '../../components/Netzstreifen'
import { rueckfrage } from '../../components/Rueckfrage'
import { ApiError } from '../../api/client'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import {
  useCreateInvitation,
  useRevokeInvitation,
  useDecideRequest,
  useInvitations,
  useJoinRequests,
  useLeaveTrip,
  useMembers,
  useRemoveMember,
  useSetAktiv,
  useSetApproval,
  useSetCutoff,
  useSetMuted,
  useMe,
  useSetTripState,
  useTrip,
  useUpdateTrip,
} from '../../hooks/queries'
import type { JoinRequest, Trip, TripState } from '../../api/types'
import { dayMonth, initials, tripPeriod } from '../../lib/format'
import { versatzAuswahl, versatzText } from '../../lib/ortszeit'
import { BackIcon, GlockeAusIcon, GlockeIcon, MondIcon, PauseIcon, PlayIcon, XIcon }
  from '../../components/Icons'
import { Erreichbarkeit } from '../../components/Erreichbarkeit'

/**
 * Die drei Zustände in Worten.
 *
 * Die Rückfrage steht dort, wo etwas verschwindet -- beim Öffnen und beim
 * Abschließen braucht es keine, beim Zurückziehen schon.
 */
const STUFEN: Array<{ wert: TripState; titel: string; text: string; frage?: string }> = [
  {
    wert: 'active',
    titel: 'Läuft',
    text: 'Du schreibst Beiträge, die Empfänger antworten.',
  },
  {
    wert: 'closed',
    // **Der Zustand heißt nach dem, was er sperrt** (#26). „Abgeschlossen" hat
    // ein Ende behauptet und deshalb nur die Hälfte seiner Verwendung getroffen:
    // aus dem Betrieb kommt dieselbe Sperre auch *vor* der Reise, damit alle
    // schon eingeladen sind und noch niemand schreibt.
    titel: 'Chat geschlossen',
    text:
      'Die Reise hat noch nicht begonnen oder ist schon beendet. Niemand ' +
      'schreibt – auch du nicht. Gelesen wird der Bericht weiter von allen, ' +
      'und du kannst die Karte weiter bearbeiten.',
    frage:
      'Den Chat dieser Reise schließen?\n\nDanach kann niemand mehr schreiben, auch du nicht. ' +
      'Gelesen werden kann der Bericht weiterhin von allen.\n\nDeine Karte bleibt offen: ' +
      'Punkte lassen sich weiter anlegen, ändern und freigeben – erzählt werden ' +
      'Freigaben im Chat dann aber nicht mehr.',
  },
  {
    wert: 'private',
    titel: 'Nur noch für mich',
    text:
      'Zusätzlich verschwindet die Reise aus den Listen aller Mitlesenden – Chat ' +
      'und Karte. Sie steht nur noch bei dir, und bearbeiten kannst du sie weiter.',
    frage:
      'Diese Reise zurückziehen?\n\nSie verschwindet aus den Listen aller Mitlesenden und ' +
      'steht nur noch bei dir. Gelöscht wird nichts: Nachrichten und Empfänger bleiben, ' +
      'und du kannst sie jederzeit wieder freigeben.',
  },
]

/**
 * Titel, Beschreibung und Reisezeitraum ändern.
 *
 * Aufgeklappt nur auf Wunsch: die Reiseseite ist ohnehin lang, und geändert
 * wird selten. Geschickt wird nur, was sich wirklich geändert hat — der
 * Server unterscheidet „nicht mitgeschickt" (lass es) von „leer
 * mitgeschickt" (lösch es), und diesen Unterschied soll das Formular nicht
 * durch Fleiß zunichtemachen.
 */
function TripForm({ trip }: { trip: Trip }) {
  const [offen, setOffen] = useState(false)
  const [title, setTitle] = useState(trip.title)
  const [description, setDescription] = useState(trip.description)
  const [von, setVon] = useState(trip.starts_on ?? '')
  const [bis, setBis] = useState(trip.ends_on ?? '')
  const [zone, setZone] = useState(
    trip.tz_offset_min === null ? '' : String(trip.tz_offset_min),
  )
  const update = useUpdateTrip(trip.id)

  if (!offen) {
    return (
      <button
        type="button"
        className="button button--outline"
        onClick={() => setOffen(true)}
      >
        Angaben ändern
      </button>
    )
  }

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault()
        const geaendert: Parameters<typeof update.mutate>[0] = {}
        if (title !== trip.title) geaendert.title = title
        if (description !== trip.description) geaendert.description = description
        if (von !== (trip.starts_on ?? '')) geaendert.starts_on = von || null
        if (bis !== (trip.ends_on ?? '')) geaendert.ends_on = bis || null
        if (zone !== (trip.tz_offset_min === null ? '' : String(trip.tz_offset_min))) {
          geaendert.tz_offset_min = zone === '' ? '' : Number(zone)
        }

        if (Object.keys(geaendert).length === 0) {
          setOffen(false)
          return
        }
        update.mutate(geaendert, { onSuccess: () => setOffen(false) })
      }}
    >
      {update.isError && <p className="notice" role="alert">{update.error.message}</p>}

      <label className="field" style={{ marginBottom: 0 }}>
        <span className="field__label">Titel</span>
        <input
          className="field__input"
          required
          maxLength={120}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>

      <label className="field" style={{ marginBottom: 0 }}>
        <span className="field__label">Beschreibung</span>
        <textarea
          className="field__input"
          rows={3}
          maxLength={2000}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>

      <div className="row row--wrap">
        <label className="field" style={{ flex: '1 1 150px', marginBottom: 0 }}>
          <span className="field__label">Von</span>
          <input
            className="field__input"
            type="date"
            value={von}
            max={bis || undefined}
            onChange={(event) => setVon(event.target.value)}
          />
        </label>
        <label className="field" style={{ flex: '1 1 150px', marginBottom: 0 }}>
          <span className="field__label">Bis</span>
          <input
            className="field__input"
            type="date"
            value={bis}
            min={von || undefined}
            onChange={(event) => setBis(event.target.value)}
          />
        </label>
      </div>

      {/* **Die Uhr der Reise.** Ohne sie rechnet die Oberfläche jeden
          Zeitpunkt in die Uhr des Geräts um — wer den Bericht abends zu Hause
          durchsieht, läse jede Ankunft um Stunden verschoben. Sie gilt für
          alle Mitlesenden; einzelne Punkte lassen sich im Formular auf der
          Karte davon abweichend stellen. */}
      <label className="field" style={{ marginBottom: 0 }}>
        <span className="field__label">Zeitzone der Reise</span>
        <select
          className="field__input"
          value={zone}
          onChange={(event) => setZone(event.target.value)}
        >
          <option value="">Keine Angabe — Zeit des jeweiligen Geräts</option>
          {versatzAuswahl().map((minuten) => (
            <option key={minuten} value={minuten}>
              {versatzText(minuten)}
            </option>
          ))}
        </select>
        <span className="field__hint">
          Zeiten an Highlights stehen dann in dieser Uhr, bei allen gleich.
        </span>
      </label>

      <div className="row">
        <button className="button" type="submit" disabled={update.isPending}>
          {update.isPending ? 'Wird gespeichert …' : 'Speichern'}
        </button>
        <button
          className="button button--outline"
          type="button"
          onClick={() => {
            setTitle(trip.title)
            setDescription(trip.description)
            setVon(trip.starts_on ?? '')
            setBis(trip.ends_on ?? '')
            setZone(trip.tz_offset_min === null ? '' : String(trip.tz_offset_min))
            setOffen(false)
          }}
        >
          Abbrechen
        </button>
      </div>
    </form>
  )
}

/** Name und Adresse dessen, der anklopft. */
function Anfragender({ request }: { request: JoinRequest }) {
  return (
    <div className="row">
      <span className="avatar" style={{ width: 36, height: 36, fontSize: 14 }}>
        {initials(request.user.display_name)}
      </span>
      <span className="bar__grow">
        <span style={{ fontWeight: 600 }}>{request.user.display_name}</span>
        <br />
        {/* Die Adresse steht dabei, weil zwei Leute denselben Namen eintragen
            können und man beim Bestätigen nicht raten soll. */}
        <span style={{ fontSize: 13, color: 'var(--text-muted)', wordBreak: 'break-all' }}>
          {request.email}
        </span>
      </span>
    </div>
  )
}

/**
 * Angaben zur Reise, Einladungen und Teilnehmer.
 *
 * Teilnehmerliste und Einladungen sieht nur der Absender -- Empfänger sollen
 * einander nicht kennen. Das Backend verweigert die Auskunft ohnehin; hier
 * wird sie gar nicht erst angefragt.
 */
export function TripInfoPage() {
  const { tripId = '' } = useParams()
  const id = Number(tripId)
  const trip = useTrip(id)
  const isSender = trip.data?.role === 'sender'
  const aussteigen = useLeaveTrip(id)
  const navigate = useNavigate()

  const members = useMembers(id, isSender)
  const invitations = useInvitations(id, isSender)
  const createInvitation = useCreateInvitation(id)
  const revoke = useRevokeInvitation(id)
  const setTripState = useSetTripState(id)
  const removeMember = useRemoveMember(id)
  const setMuted = useSetMuted(id)
  const requests = useJoinRequests(id, isSender)
  const decide = useDecideRequest(id)
  const setApproval = useSetApproval(id)
  // Für die Marke „an alle" -- dieselbe Route wie Titel und Zeitraum.
  const updateTrip = useUpdateTrip(id)
  const setCutoff = useSetCutoff(id)
  const setAktiv = useSetAktiv(id)
  // Ob die zweite Ebene überhaupt angeboten wird: über ein Konto entscheidet
  // die Verwaltung, nicht der Absender einer Reise.
  const me = useMe()
  const [copied, setCopied] = useState<string | null>(null)

  /*
   * **Vom Sperrbildschirm direkt ans Freigabeformular (#46).**
   *
   * Aus dem Issue: „Kann die Chat-Nachricht direkt den Link enthalten für das
   * Benutzer/Freigabeformular?" Der Push zeigte schon auf diese Seite -- die
   * Karte „Möchte mitlesen" steht darauf aber unter den anderen, und auf dem
   * Telefon heißt das: suchen.
   *
   * **Ein Suchparameter und kein Anker**, und das ist keine Geschmacksfrage:
   * `alsPfad()` in `lib/tippziel.ts` gibt `pathname + search` zurück und wirft
   * den Hash weg. Ein `#anfragen` käme genau dann nie an, wenn die App schon
   * offen ist -- also im Normalfall.
   */
  const [suche, setSuche] = useSearchParams()
  const anfragenZeigen = suche.get('anfragen') === '1'
  const anfragenKarte = useRef<HTMLDivElement>(null)
  // Ein `useRef` und kein Zustand: er wird nirgends gezeichnet, sondern hält
  // nur fest, dass es schon passiert ist.
  const gesprungen = useRef(false)

  useEffect(() => {
    if (!anfragenZeigen || gesprungen.current) return
    // **Erst wenn die Anfragen geladen sind.** Die Karte steht sonst noch gar
    // nicht da, und ein Sprung ins Leere sähe aus wie keiner.
    if (anfragenKarte.current === null) return

    gesprungen.current = true
    anfragenKarte.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
    // Der Parameter geht wieder weg, sonst springt jedes Neuladen erneut.
    setSuche({}, { replace: true })
  }, [anfragenZeigen, requests.data, setSuche])
  const zeitraum = tripPeriod(trip.data?.starts_on ?? null, trip.data?.ends_on ?? null)
  const wartende = requests.data?.filter((r) => r.state === 'pending') ?? []
  const abgelehnte = requests.data?.filter((r) => r.state === 'rejected') ?? []
  // Welche der drei Stufen gerade gilt. `hidden_at` wiegt schwerer, weil es
  // `archived_at` voraussetzt.
  const zustand: TripState =
    trip.data?.hidden_at != null ? 'private' : trip.data?.archived_at != null ? 'closed' : 'active'

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(url)
      setTimeout(() => setCopied(null), 2500)
    } catch {
      // Ohne Zwischenablage-Recht bleibt der Link sichtbar zum Markieren.
      setCopied(null)
    }
  }

  return (
    <>
      <header className="bar">
        <Link className="icon-button" to={`/t/${id}`} aria-label="Zurück zum Chat">
          <BackIcon />
        </Link>
        <h2 className="bar__title bar__grow">{trip.data?.title ?? 'Reise'}</h2>
      </header>
      <Netzstreifen />

      <div className="scroll-panel">
        <div className="card card--seite">
          {zeitraum !== '' && (
            <p style={{ margin: '0 0 8px', fontWeight: 600 }}>{zeitraum}</p>
          )}
          {trip.data?.description && <p>{trip.data.description}</p>}
          <p className="card__lead" style={{ marginBottom: isSender ? 12 : 0 }}>
            {isSender
              ? 'Du bist der Absender dieser Reise.'
              : `Reisebericht von ${trip.data?.owner.display_name ?? ''}.`}
          </p>

          {/* Auch eine abgeschlossene Reise lässt sich noch beschriften: die
              Beschreibung entsteht oft erst, wenn sie vorbei ist. */}
          {isSender && trip.data && (
            <TripForm key={trip.data.id} trip={trip.data} />
          )}
        </div>

        {/* Direkt unter den Angaben: es gehört zum Bild der Reise nach außen,
            wie Titel und Zeitraum. Und es steht nur beim Reisenden. */}
        {isSender && trip.data && <Symbolbild trip={trip.data} />}

        {isSender && trip.data && <Chatgrund trip={trip.data} />}

        <div className="card card--seite">
          <h2 className="card__titel">Benachrichtigung</h2>
          {/* **Die Wirkung, die man eigentlich sucht, stand hier nicht.** Der
              Schalter regelt nicht nur die Hervorhebung in der Liste, sondern
              auch, ob für diese Reise Push-Nachrichten aufs Gerät kommen — und
              genau deshalb sucht man ihn. Aus dem Betrieb gemeldet. */}
          <p className="card__lead">
            {trip.data?.muted
              ? 'Diese Reise ist stummgeschaltet: keine Push-Nachrichten aufs Gerät, und keine Hervorhebung in der Chatliste. Neue Nachrichten stehen weiter da, nur still.'
              : 'Neue Nachrichten werden in der Chatliste hervorgehoben, und du bekommst eine Push-Nachricht aufs Gerät. Stummschalten hält beides an — für diese Reise, jederzeit umkehrbar.'}
          </p>
          <button
            className="button button--outline"
            type="button"
            disabled={setMuted.isPending || trip.data === undefined}
            onClick={() => setMuted.mutate(!trip.data?.muted)}
          >
            {trip.data?.muted ? 'Stummschaltung aufheben' : 'Stummschalten'}
          </button>
        </div>

        {/* **Raus heißt raus** -- und nur für Mitlesende. Dem Absender gehört
            die Reise; für ihn gibt es „abschließen" und „zurückziehen"
            weiter unten. */}
        {!isSender && (
          <div className="card card--seite">
            <h2 className="card__titel">Diese Reise verlassen</h2>
            <p className="card__lead">
              Du liest dann nicht mehr mit. Was du geschrieben hast, bleibt beim
              Reisenden stehen — du siehst es nicht mehr. <strong>Zurück geht es
              nur über eine neue Einladung</strong>, und auch dann muss der
              Reisende zustimmen.
            </p>
            <button
              className="button button--outline"
              type="button"
              disabled={aussteigen.isPending}
              onClick={async () => {
                if (
                  await rueckfrage(
                    `Aus „${trip.data?.title ?? 'dieser Reise'}" aussteigen? Du liest dann ` +
                      'nicht mehr mit. Zurück geht es nur über eine neue Einladung des ' +
                      'Reisenden — dieser Schritt lässt sich nicht selbst rückgängig machen.',
                    'Aussteigen',
                  )
                ) {
                  // **Erst weg, dann aufräumen.** Nach dem Ausstieg gibt es
                  // diese Reise für dieses Konto nicht mehr; bliebe man auf
                  // ihrer Seite stehen, liefe sie in ein 404. `replace`,
                  // damit der Zurück-Knopf nicht dorthin zurückführt.
                  await aussteigen.mutateAsync()
                  navigate('/', { replace: true })
                }
              }}
            >
              {aussteigen.isPending ? 'Einen Moment …' : 'Aussteigen'}
            </button>
            {aussteigen.isError && (
              <p className="notice" role="alert">
                {aussteigen.error instanceof Error
                  ? aussteigen.error.message
                  : 'Das hat nicht geklappt.'}
              </p>
            )}
          </div>
        )}

        {isSender && (
          <>
            {/* Ganz oben, sobald jemand wartet: eine Anfrage, die man
                übersieht, ist für den Anfragenden nicht von einer
                Ablehnung zu unterscheiden.

                Die abgelehnten stehen mit dabei, still und weiter unten. Eine
                Entscheidung, die man nicht mehr sieht, lässt sich nicht
                revidieren -- und man weiß später nicht einmal mehr, dass man
                sie getroffen hat. */}
            {(requests.data?.length ?? 0) > 0 && (
              <div className="card card--seite" ref={anfragenKarte}>
                {/* Ohne Wartende trägt die Karte den zweiten Titel -- „Möchte
                    mitlesen (0)" wäre eine Überschrift über nichts. */}
                <h2 className="card__titel">
                  {wartende.length > 0
                    ? `Möchte mitlesen (${wartende.length})`
                    : 'Abgelehnte Anfragen'}
                </h2>
                {wartende.length > 0 && (
                  <p className="card__lead">
                    Diese Personen haben deinen Einladungslink geöffnet. Erst mit
                    deiner Bestätigung lesen sie den Bericht.
                  </p>
                )}

                {decide.isError && <p className="notice" role="alert">{decide.error.message}</p>}

                <div className="stack">
                  {wartende.map((request) => (
                    <div
                      key={request.user.id}
                      className="stack"
                      style={{ gap: 8, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}
                    >
                      <Anfragender request={request} />

                      <div className="row">
                        <button
                          type="button"
                          className="button button--small"
                          disabled={decide.isPending}
                          onClick={async () => {
                            /*
                              **Die Grenze des Links fragt, sie sperrt nicht.**

                              Sie soll verhindern, dass ein weitergereichter
                              Link ohne deine Entscheidung viele hereinlässt.
                              Stehst du selbst davor, ist die Entscheidung da --
                              dir dann nein zu sagen wäre ein kaputter Knopf,
                              und der Anwärter bliebe für immer hängen.

                              Der Server antwortet beim ersten Versuch mit 409;
                              erst danach wird gefragt. Die Rückfrage vorweg zu
                              stellen hieße, die Regel ein zweites Mal im
                              Browser zu führen.
                            */
                            try {
                              await decide.mutateAsync({
                                userId: request.user.id,
                                approve: true,
                              })
                            } catch (fehler) {
                              if (!(fehler instanceof ApiError) || fehler.status !== 409) return
                              if (
                                await rueckfrage(
                                  `${fehler.message} ${request.user.display_name} ` +
                                    'trotzdem aufnehmen?',
                                  'Trotzdem aufnehmen',
                                )
                              ) {
                                decide.mutate({
                                  userId: request.user.id,
                                  approve: true,
                                  trotzdem: true,
                                })
                              }
                            }
                          }}
                        >
                          Aufnehmen
                        </button>
                        <button
                          type="button"
                          className="button button--outline button--small"
                          disabled={decide.isPending}
                          onClick={async () => {
                            if (
                              await rueckfrage(
                                `${request.user.display_name} für diese Reise ablehnen? ` +
                                  'Sie erfährt davon nichts — für sie sieht es weiter ' +
                                  'so aus, als stünde die Bestätigung noch aus. Das Konto ' +
                                  'bleibt bestehen, und du kannst es dir später anders ' +
                                  'überlegen.',
                                'Ablehnen',
                              )
                            ) {
                              decide.mutate({ userId: request.user.id, approve: false })
                            }
                          }}
                        >
                          Nur für diese Reise ablehnen
                        </button>
                      </div>

                      {/* Die zweite Ebene, und nur für die Verwaltung: wer
                          über einen weitergereichten Link hereinkommt, soll
                          nicht bei der nächsten Reise wieder vor der Tür
                          stehen. Das Konto geht über diese Reise hinaus --
                          deshalb entscheidet darüber nicht der Absender. */}
                      {me.data?.may_manage_people && (
                        <button
                          type="button"
                          className="button button--outline button--small button--gefahr"
                          style={{ alignSelf: 'flex-start' }}
                          disabled={decide.isPending}
                          onClick={async () => {
                            if (
                              await rueckfrage(
                                `${request.user.display_name} ablehnen und das Konto sperren? ` +
                                  'Für den Fall, dass jemand über einen weitergereichten ' +
                                  'Link hereinkommt, den du nicht kennst. Sie wird sofort ' +
                                  'abgemeldet und kommt nirgends mehr herein — auch nicht ' +
                                  'in andere Reisen. Unter Einstellungen → Personen lässt ' +
                                  'sich das zurücknehmen.',
                                'Ablehnen und sperren',
                              )
                            ) {
                              decide.mutate({
                                userId: request.user.id,
                                approve: false,
                                disableAccount: true,
                              })
                            }
                          }}
                        >
                          Ablehnen und Konto sperren
                        </button>
                      )}
                    </div>
                  ))}

                  {abgelehnte.length > 0 && (
                    <div className="stack" style={{ gap: 8 }}>
                      {wartende.length > 0 && (
                        <h2 style={{ fontSize: 14, margin: '4px 0 0', color: 'var(--text-muted)' }}>
                          Abgelehnt ({abgelehnte.length})
                        </h2>
                      )}
                      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
                        Sie warten weiterhin auf eine Bestätigung, die nie kommt –
                        davon erfahren sie nichts. Du kannst sie trotzdem noch
                        aufnehmen.
                      </p>

                      {abgelehnte.map((request) => (
                        <div
                          key={request.user.id}
                          className="stack"
                          style={{ gap: 6, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}
                        >
                          <Anfragender request={request} />
                          <button
                            type="button"
                            className="button button--outline button--small"
                            style={{ alignSelf: 'flex-start' }}
                            disabled={decide.isPending}
                            onClick={() =>
                              decide.mutate({ userId: request.user.id, approve: true })
                            }
                          >
                            Doch aufnehmen
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="card card--seite">
              <h2 className="card__titel">Einladen</h2>
              <p className="card__lead">
                {trip.data?.approval_required
                  ? 'Teile den Link auf beliebigem Weg. Wer ihn öffnet, meldet sich bei dir – und liest mit, sobald du zustimmst.'
                  : 'Teile den Link auf beliebigem Weg. Wer ihn öffnet, legt ein Konto an und liest ab dann mit.'}
              </p>

              {invitations.data?.map((invitation) => (
                <div key={invitation.code} className="stack" style={{ marginBottom: 14 }}>
                  <div className="row">
                    <span className="code">{invitation.code}</span>
                    {/* **Was hier stand, war wahr und trotzdem irreführend.**
                        `uses` steigt erst mit deiner Freigabe -- also stand
                        „noch nicht benutzt" da, während vier Menschen auf eine
                        Antwort warteten. Gemeldet aus dem Betrieb, mit
                        Bildschirmfoto. Wer wartet, gehört zuerst genannt: das
                        ist das, was eine Handlung verlangt. */}
                    <span className="feld__hinweis feld__hinweis--13">
                      {invitation.wartend > 0
                        ? invitation.wartend === 1
                          ? 'einer wartet auf dich'
                          : `${invitation.wartend} warten auf dich`
                        : invitation.uses === 0
                          ? 'noch nicht benutzt'
                          : invitation.uses === 1
                            ? 'einmal benutzt'
                            : `${invitation.uses}-mal benutzt`}
                    </span>
                  </div>
                  {/* Was der Link kann, und was er nicht ist: keine Obergrenze
                      für die Reise, sondern für diesen einen Link. Die alte
                      Zeile „1 von 25 eingelöst" las sich wie eine Grenze für
                      die Zahl der Mitlesenden. */}
                  <span className="feld__hinweis feld__hinweis--13">
                    {invitation.max_uses === 1
                      ? 'Gilt für eine Person'
                      : `Gilt für bis zu ${invitation.max_uses} Beitritte`}
                    {' · läuft am '}
                    {dayMonth(invitation.expires_at)} ab
                  </span>
                  <div className="row">
                    <button
                      className="button button--outline"
                      type="button"
                      onClick={() => void copy(invitation.url)}
                    >
                      {copied === invitation.url ? 'Kopiert' : 'Link kopieren'}
                    </button>
                    {/* Ein Link, der aus der Hand ist, lässt sich nicht
                        zurückholen -- ihn ungültig zu machen ist die einzige
                        Antwort darauf. Mit Rückfrage, denn rückgängig macht
                        das niemand: wer den Code schon hat, steht danach vor
                        einer Tür, die nicht mehr aufgeht. */}
                    <button
                      className="button button--outline button--small"
                      type="button"
                      disabled={revoke.isPending}
                      onClick={async () => {
                        if (
                          await rueckfrage(
                            'Diesen Link deaktivieren? ' +
                              'Wer ihn noch nicht eingelöst hat, kommt damit nicht mehr herein. ' +
                              'Wer schon dabei ist, bleibt dabei. Rückgängig geht das nicht — ' +
                              'für neue Einladungen brauchst du einen neuen Link.',
                            'Deaktivieren',
                          )
                        ) {
                          revoke.mutate(invitation.code)
                        }
                      }}
                    >
                      Link deaktivieren
                    </button>
                  </div>
                  {revoke.isError && <p className="notice" role="alert">{revoke.error.message}</p>}
                  <code style={{ fontSize: 12, color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                    {invitation.url}
                  </code>
                </div>
              ))}

              {/* Ein Link in eine zurückgezogene Reise führt ins Leere. Das
                  gehört hierher und nicht erst in die Fehlermeldung dessen,
                  der ihn öffnet. */}
              {zustand === 'private' && (
                <p className="notice notice--info">
                  Solange die Reise nur für dich steht, führt kein Link hinein.
                </p>
              )}

              <div className="row">
                <button
                  className="button"
                  type="button"
                  disabled={createInvitation.isPending || zustand === 'private'}
                  onClick={() => createInvitation.mutate(undefined)}
                >
                  {createInvitation.isPending ? 'Einen Moment …' : 'Neuen Link erzeugen'}
                </button>
                {/* Ein Link, der nach dem ersten Beitritt verbraucht ist:
                    weitergereicht nützt er dann niemandem mehr. */}
                <button
                  className="button button--outline"
                  type="button"
                  disabled={createInvitation.isPending || zustand === 'private'}
                  onClick={() => createInvitation.mutate(1)}
                >
                  Link für eine Person
                </button>
              </div>

              {/* Direkt beim Link, weil er die Frage aufwirft: ein Link wird
                  weitergereicht, und dieser Schalter entscheidet, ob das
                  genügt. */}
              <label
                className="row"
                style={{ marginTop: 16, alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={trip.data?.approval_required ?? true}
                  disabled={setApproval.isPending}
                  onChange={(event) => setApproval.mutate(event.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <span style={{ fontWeight: 600 }}>Beitritt bestätigen</span>
                  <br />
                  <span className="feld__hinweis feld__hinweis--13">
                    {trip.data?.approval_required
                      ? 'Wer den Link öffnet, kommt erst herein, wenn du zustimmst. Auch mit neuem Konto.'
                      : 'Wer den Link hat, liest sofort mit – auch wenn ihn jemand weitergibt.'}
                  </span>
                </span>
              </label>
            </div>

            <div className="card card--seite" style={{ marginBottom: 0 }}>
              <h2 className="card__titel">
                Empfänger (
                {members.data?.filter((m) => m.role === 'recipient' && !m.left).length ?? 0})
              </h2>
              <p className="card__lead">
                Die Empfänger sehen diese Liste nicht – und auch nicht die
                Antworten der anderen.
              </p>

              {/* **Eine Legende, einmal oben** (#32).

                  Symbole in jeder Zeile brauchen eine Stelle, an der steht,
                  was sie bedeuten. Ein `title` genügt dafür nicht: auf dem
                  Telefon ist er unsichtbar — dieselbe Regel wie bei den
                  Fehlermeldungen. Also einmal hier in Worten, und in den
                  Zeilen nur noch das Bild. */}
              <p className="feld__hinweis feld__hinweis--13 zeichenerklaerung">
                <span><GlockeIcon size={15} /> bekommt Benachrichtigungen</span>
                <span><GlockeAusIcon size={15} /> stummgeschaltet</span>
                <span><MondIcon size={15} /> stumm, ohne Benachrichtigungen</span>
              </p>

              {removeMember.isError && (
                <p className="notice" role="alert">{removeMember.error.message}</p>
              )}

              <div className="stack">
                {members.data
                  ?.filter((member) => member.role === 'recipient')
                  .map((member) => (
                    <div
                      key={member.user.id}
                      className="stack"
                      style={{ gap: 6, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}
                    >
                      <div className="row">
                        <Link
                          className="row bar__grow"
                          to={`/t/${id}/with/${member.user.id}`}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <span className="avatar" style={{ width: 36, height: 36, fontSize: 14 }}>
                            {initials(member.user.display_name)}
                          </span>
                          <span style={member.disabled ? { fontStyle: 'italic' } : undefined}>
                            {member.user.display_name}
                            {/* Selbst gegangen. Zuerst, weil es alles andere
                                erklärt: an dieser Zeile hängt nichts mehr. */}
                            {member.left && (
                              <span className="feld__hinweis feld__hinweis--13">
                                {' '}
                                · ausgestiegen
                              </span>
                            )}
                            {/* Stillgelegt zuerst: solange das Konto zu ist,
                                ist alles andere nachrangig. */}
                            {member.disabled && !member.left && (
                              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                                {' '}
                                · stillgelegt
                              </span>
                            )}
                            {member.cut_off && (
                              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                                {' '}
                                · bekommt nichts Neues
                              </span>
                            )}
                          </span>
                          {/* **Erreichbarkeit und Ruhe, als ein Zeichen** (#32).

                              Wer gegangen oder stillgelegt ist, bekommt keins:
                              dort sagt das Wort daneben schon alles, und ein
                              zweites Zeichen wäre eine Aussage über jemanden,
                              der nicht mehr dabei ist. Und wer erreichbar ist
                              und nichts stummgeschaltet hat, bekommt auch
                              keins — der Normalfall braucht kein Abzeichen. */}
                          {!member.left && !member.disabled && (
                            <Erreichbarkeit
                              push={member.push === true}
                              stumm={member.muted === true}
                            />
                          )}
                        </Link>

                        {/* Wer gegangen ist, lässt sich weder entfernen noch
                            abschneiden -- er ist kein Mitglied mehr. Die Zeile
                            steht nur noch da, damit niemand spurlos fehlt. */}
                        {!member.left && <button
                          type="button"
                          className="icon-button"
                          title={`${member.user.display_name} entfernen`}
                          aria-label={`${member.user.display_name} entfernen`}
                          disabled={removeMember.isPending}
                          onClick={async () => {
                            // Was sie geschrieben hat, bleibt dir erhalten --
                            // das gehört in die Frage hinein, sonst zögert man
                            // aus dem falschen Grund.
                            if (
                              await rueckfrage(
                                `${member.user.display_name} aus dieser Reise entfernen? ` +
                                  'Sie kann den Bericht dann nicht mehr lesen. Ihre bisherigen ' +
                                  'Antworten bleiben für dich sichtbar.',
                                'Entfernen',
                              )
                            ) {
                              removeMember.mutate(member.user.id)
                            }
                          }}
                        >
                          <XIcon size={16} />
                        </button>}
                      </div>

                      {/* Der leise Weg neben dem sichtbaren -- und mit Worten,
                          nicht nur mit einem Zeichen. Ein Pausesymbol neben
                          einem Namen sagt niemandem, was es bewirkt, und ein
                          Knopf, den man nicht findet, ist keiner. */}
                      {!member.left && <button
                        type="button"
                        className="button button--outline button--small"
                        style={{ alignSelf: 'flex-start' }}
                        disabled={setCutoff.isPending}
                        onClick={async () => {
                          if (
                            member.cut_off ||
                            (await rueckfrage(
                              `${member.user.display_name} nichts Neues mehr schicken? ` +
                                'Sie merkt nichts davon: der Bericht bleibt für sie stehen, ' +
                                'wie er jetzt ist, und sieht aus wie einer, der eingeschlafen ' +
                                'ist. Jederzeit rückgängig zu machen.',
                              'Nichts Neues mehr',
                            ))
                          ) {
                            setCutoff.mutate({ userId: member.user.id, cut: !member.cut_off })
                          }
                        }}
                      >
                        {member.cut_off ? <PlayIcon size={15} /> : <PauseIcon size={15} />}
                        {member.cut_off ? 'Wieder beliefern' : 'Nichts Neues mehr schicken'}
                      </button>}
                    </div>
                  ))}

                {members.data?.every((member) => member.role !== 'recipient') && (
                  <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                    Noch niemand beigetreten.
                  </p>
                )}
              </div>
            </div>

            {/* **Die Marke „aktiv" — genau eine, und nur an einer laufenden.**
                Sie steht hier neben dem Zustand, weil sie eine Aussage
                derselben Art ist: nicht, was jemand darf, sondern woran du
                gerade bist. Angeboten wird sie nur an einer laufenden Reise —
                der Server weist alles andere ab, und ein Knopf, der abgewiesen
                wird, ist eine stille Absage mit Umweg.

                Der Zustand darunter heißt „Läuft", diese Marke heißt „aktiv":
                laufen können mehrere, unterwegs bist du auf einer. */}
            {zustand === 'active' && (
              <div className="card" style={{ margin: '16px auto 0', boxShadow: 'none' }}>
                <h2 className="card__titel">Aktive Reise</h2>
                <p className="card__lead">
                  {trip.data?.activated_at != null
                    ? 'Diese Reise steht bei allen Mitlesenden oben in der Liste; die übrigen fassen sich darunter zusammen und sind zugeklappt.'
                    : 'Markiere die Reise, auf der du gerade unterwegs bist. Sie steht dann bei allen Mitlesenden oben in der Liste, und die übrigen Reisen fassen sich darunter zusammen. Es kann immer nur eine sein — markierst du eine andere, wandert die Marke mit. Beim Abschließen oder Zurückziehen fällt sie weg.'}
                </p>

                {setAktiv.isError && (
                  <p className="notice" role="alert">{setAktiv.error.message}</p>
                )}

                <button
                  className="button button--outline"
                  type="button"
                  disabled={setAktiv.isPending || trip.data === undefined}
                  onClick={() => setAktiv.mutate(trip.data?.activated_at == null)}
                >
                  {trip.data?.activated_at != null
                    ? 'Marke wieder lösen'
                    : 'Als aktive Reise markieren'}
                </button>
              </div>
            )}

            {/* Drei Zustände, eine Leiter: jeder nimmt etwas mehr weg, und
                keiner löscht etwas. Als Auswahl und nicht als Schalterkette,
                weil man den Unterschied nur sieht, wenn alle drei
                nebeneinander stehen. */}
            {/* **Trägt auch ein Beitrag an alle seine Marke?**

                Aus dem Betrieb: „beide markieren, aber die Option auch im
                Broadcast konfigurierbar machen für den Admin - auf
                Reise-Ebene, nicht generell." Eine Aussage über *diesen*
                Bericht, deshalb hier und nicht in den Einstellungen des
                Kontos: wer zwei Reisen zugleich führt, eine stille und eine
                mit vielen Einzelnachrichten, will es hier so und dort anders.

                **Eine eigene Karte und nicht bei „Einladen".** Dort stand sie
                zuerst, weil daneben schon eine Ankreuzfläche war -- mit dem
                Einladen hat sie aber nichts zu tun. Aufgefallen ist es, weil
                ein Prüflauf die *erste* Ankreuzfläche der Seite griff und
                damit die falsche traf.

                Die private Nachricht trägt ihre Marke unabhängig davon --
                sie ist die Ausnahme, und die gehört benannt. */}
            <div className="card card--seite">
              <h2 className="card__titel">Kennzeichnung</h2>
              <label
                className="row"
                style={{ alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={trip.data?.mark_broadcast ?? true}
                  disabled={updateTrip.isPending}
                  onChange={(event) =>
                    updateTrip.mutate({ mark_broadcast: event.target.checked })
                  }
                  style={{ marginTop: 3 }}
                />
                <span>
                  <span style={{ fontWeight: 600 }}>Beiträge an alle kennzeichnen</span>
                  <br />
                  <span className="feld__hinweis feld__hinweis--13">
                    {(trip.data?.mark_broadcast ?? true)
                      ? 'Jede Nachricht sagt, wie weit sie reicht — an alle oder an einen.'
                      : 'Nur Nachrichten an einen Einzelnen sind gekennzeichnet.'}
                  </span>
                </span>
              </label>
            </div>

            <div className="card" style={{ margin: '16px auto 0', boxShadow: 'none' }}>
              <h2 className="card__titel">Zustand der Reise</h2>

              {setTripState.isError && (
                <p className="notice" role="alert">{setTripState.error.message}</p>
              )}

              <div className="stack" style={{ gap: 0 }}>
                {STUFEN.map((stufe) => (
                  <label key={stufe.wert} className="choice">
                    <input
                      type="radio"
                      name="trip-state"
                      checked={zustand === stufe.wert}
                      disabled={setTripState.isPending}
                      onChange={async () => {
                        if (
                          stufe.frage === undefined ||
                          zustand === stufe.wert ||
                          (await rueckfrage(stufe.frage, stufe.titel))
                        ) {
                          setTripState.mutate(stufe.wert)
                        }
                      }}
                    />
                    <span>
                      <span style={{ fontWeight: 600 }}>{stufe.titel}</span>
                      <br />
                      <span className="feld__hinweis feld__hinweis--13">
                        {stufe.text}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* **Ganz unten und nur für den Inhaber** (#12). Was den Bericht
                wegwirft, steht nicht zwischen den Einstellungen, sondern am
                Ende — und `role === 'sender'` genügt dafür nicht: eine Reise
                kann mehrere Absender haben, gehören tut sie einem. Der Server
                prüft dasselbe noch einmal. */}
            {trip.data !== undefined && trip.data.owner.id === me.data?.id && (
              <Zuruecksetzen tripId={id} titel={trip.data.title} />
            )}
          </>
        )}
      </div>
    </>
  )
}
